import { useCallback, useMemo } from "react";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { LinkIcon, XIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import Select from "@/components/shared/select";
import { Tooltip } from "@/components/shared/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FieldReferenceInputProps, FieldReferenceOption } from "./PostAnswerActions.model";
import styles from "./PostAnswerActionsConfiguration.module.scss";

const REFERENCE_PATTERN = /^@\[(.*)\]\((.*)\)$/;

function parseReference(value: string): { label: string; id: string } | null {
    const match = value.match(REFERENCE_PATTERN);
    if (!match) return null;
    return { label: match[1], id: match[2] };
}

export function FieldReferenceInput({
    value,
    onChange,
    fieldReferenceOptions,
    placeholder = "Enter a value...",
    className,
    selectOptions,
}: FieldReferenceInputProps) {
    const stringValue = typeof value === "string" ? value : Array.isArray(value) ? value[0] || "" : "";

    const reference = useMemo(() => parseReference(stringValue), [stringValue]);

    const ticketFields = useMemo(
        () => fieldReferenceOptions.filter((o) => o.category === "ticket"),
        [fieldReferenceOptions]
    );

    const customFields = useMemo(
        () => fieldReferenceOptions.filter((o) => o.category === "customField"),
        [fieldReferenceOptions]
    );

    const handleSelectReference = useCallback(
        (option: FieldReferenceOption) => {
            onChange(`@[${option.label}](${option.id})`);
        },
        [onChange]
    );

    const handleClearReference = useCallback(() => {
        onChange("");
    }, [onChange]);

    const referencePicker =
        fieldReferenceOptions.length > 0 ? (
            <DropdownMenu>
                <Tooltip>
                    <Tooltip.Trigger>
                        <DropdownMenuTrigger asChild>
                            <IconButton
                                variant="soft"
                                color="gray"
                                size="2"
                                highContrast
                                type="button"
                                aria-label="Set dynamic value from ticket field"
                            >
                                <LinkIcon size={14} />
                            </IconButton>
                        </DropdownMenuTrigger>
                    </Tooltip.Trigger>
                    <Tooltip.Content>Set dynamic value from ticket field</Tooltip.Content>
                </Tooltip>
                <DropdownMenuContent align="end" className={styles.fieldReferenceMenu}>
                    {ticketFields.length > 0 && (
                        <DropdownMenuGroup>
                            <DropdownMenuLabel>Ticket Data</DropdownMenuLabel>
                            {ticketFields.map((option) => (
                                <DropdownMenuItem
                                    key={option.id}
                                    onSelect={() => handleSelectReference(option)}
                                >
                                    {option.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuGroup>
                    )}
                    {ticketFields.length > 0 && customFields.length > 0 && (
                        <DropdownMenuSeparator />
                    )}
                    {customFields.length > 0 && (
                        <DropdownMenuGroup>
                            <DropdownMenuLabel>Custom Fields</DropdownMenuLabel>
                            {customFields.map((option) => (
                                <DropdownMenuItem
                                    key={option.id}
                                    onSelect={() => handleSelectReference(option)}
                                >
                                    {option.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuGroup>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        ) : null;

    if (reference) {
        return (
            <Flex
                align="center"
                gap="2"
                className={`${styles.fieldReferenceInputWrapper} ${className ?? ""}`}
            >
                <Flex align="center" gap="2" className={styles.referenceToken}>
                    <LinkIcon size={13} className={styles.referenceTokenIcon} />
                    <Text size="2" truncate className={styles.referenceTokenLabel}>
                        {reference.label || reference.id}
                    </Text>
                    <Tooltip>
                        <Tooltip.Trigger>
                            <IconButton
                                variant="ghost"
                                color="gray"
                                size="1"
                                highContrast
                                type="button"
                                onClick={handleClearReference}
                                className={styles.referenceTokenClear}
                            >
                                <XIcon size={13} />
                            </IconButton>
                        </Tooltip.Trigger>
                        <Tooltip.Content>Clear and type a value instead</Tooltip.Content>
                    </Tooltip>
                </Flex>
            </Flex>
        );
    }

    return (
        <Flex
            align="center"
            gap="2"
            className={`${styles.fieldReferenceInputWrapper} ${className ?? ""}`}
        >
            {selectOptions ? (
                <Select
                    options={selectOptions}
                    value={stringValue || undefined}
                    onChange={onChange}
                    placeholder={placeholder}
                    classes={{ root: styles.fieldReferenceControl }}
                />
            ) : (
                <Input
                    placeholder={placeholder}
                    value={stringValue}
                    onChange={(e) => onChange(e.target.value)}
                    className={`${styles.fieldReferenceControl} ${styles.fieldReferenceTextInput}`}
                />
            )}
            {referencePicker}
        </Flex>
    );
}
